import {
  ChangeDetectionStrategy,
  Component,
  Input,
  isDevMode,
  OnChanges,
  OnInit,
  SimpleChanges
} from "@angular/core";
import { Nullable } from "src/app/support/nullable";
import {
  SkeletonLoaderConfig,
  SkeletonLoaderConfigTheme
} from "./skeleton-loader-config.types";

@Component({
  selector: "pms-skeleton-loader",
  templateUrl: "skeleton-loader.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SkeletonLoaderComponent implements OnInit, OnChanges {
  private readonly skeletonProgressBaseClasses: string =
    "[transform:translate3d(0,0,0)] before:box-border before:absolute before:top-0 before:left-0 before:z-[1] before:h-full before:w-[200px] before:bg-[length:200px_100%] before:content-[''] before:animate-skeleton-progress motion-reduce:before:animate-none motion-reduce:before:bg-none";

  @Input()
  count: SkeletonLoaderConfig["count"];

  @Input()
  appearance: SkeletonLoaderConfig["appearance"];

  @Input()
  animation: SkeletonLoaderConfig["animation"];

  @Input()
  theme: Nullable<SkeletonLoaderConfigTheme>;

  // tslint:disable-next-line: no-any
  items: Array<any>;

  get loaderModifierClasses(): string {
    const parts: string[] = [];

    parts.push(
      this.appearance === "circle"
        ? "w-10 h-10 m-[5px] rounded-full"
        : "w-full rounded"
    );

    if (this.animation === "pulse") {
      parts.push("delay-500 animate-skeleton-pulse motion-reduce:animate-none");
    }

    if (this.animation === "progress") {
      parts.push(
        `${this.skeletonProgressBaseClasses} before:bg-skeleton-shimmer`
      );
    }

    if (this.animation === "progress-dark") {
      parts.push(
        `${this.skeletonProgressBaseClasses} before:bg-skeleton-shimmer-dark`
      );
    }

    return parts.join(" ");
  }

  constructor() {
    const {
      appearance = "line",
      animation = "progress",
      theme = <SkeletonLoaderConfigTheme>null,
      count = 1
    } = {};

    this.appearance = appearance;
    this.animation = animation;
    this.theme = theme;
    this.count = count;
    this.items = [];
  }

  ngOnInit(): void {
    this.validateInputValues();
  }

  private validateInputValues(): void {
    this.items.length = this.count;

    const allowedAnimations = ["progress", "progress-dark", "pulse", "false"];
    if (allowedAnimations.indexOf(String(this.animation)) === -1) {
      this.animation = "progress";
    }

    if (["circle", "line", ""].indexOf(String(this.appearance)) === -1) {
      this.appearance = "";
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Avoiding multiple calls for the same input in case there's no changes in the fields
    // Checking if the fields that require validation are available and if they were changed
    // In case were not changed, we stop the function. Otherwise, `validateInputValues` will be called.
    if (
      ["count", "animation", "appearance"].find(
        (key) =>
          changes[key] &&
          (changes[key].isFirstChange() ||
            changes[key].previousValue === changes[key].currentValue)
      )
    ) {
      return;
    }

    this.validateInputValues();
  }
}
